import { css } from '@emotion/core';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CategoryDetail, ProductDetail, ProductsFilterItem, useCategoryDetail, VirtualCategory } from '@api/catalog';
import { getInStockFilter } from '@api/catalog/product/catalogSearchParams';
import { getPagination } from '@api/common/helpers';
import { SeoData } from '@api/seo/types';

import { getSiteOrigin } from '@utils/config';
import { getCorrectPathname } from '@utils/getCorrectPathname';

import { useCityStore } from '@store/city';

import { POPULAR_SORT_VALUE } from '@components/ControlViewItems/constants';
import { readStoredCatalogSort } from '@components/ControlViewItems/helpers';
import { SortFilter } from '@components/ControlViewItems/types';
import { ExpandableBlock } from '@components/ExpandableBlock';
import { FormValues } from '@components/Filters/ProductFilter/types';
import { useCatalogFilters } from '@components/Filters/ProductFilter/useCatalogFilters';
import JsonLdScript from '@components/JsonLdScript';
import LayoutWithMargin from '@components/LayoutWithMargin';
import PageWrapper from '@components/PageWrapper';
import ProductsList from '@components/ProductsList';
import { Breadcrumbs } from '@components/uiKit/Breadcrumbs';

import { ITEMS_PER_PRODUCTS_PAGE } from '@scripts/constants';
import { VIEW_OPTIONS } from '@scripts/enums';
import { Layout, Section, typography } from '@scripts/gds';
import {
    customFlatWithoutChildren,
    declOfNum,
    getBreadcrumbs,
    getIdBySeoType,
    getTotalPages,
    isNotEmptyObject,
    safeParseFilters,
} from '@scripts/helpers';
import { useGetProductsList } from '@scripts/hooks/useGetProductsList';
import { useGetUserData } from '@scripts/hooks/useGetUserData';
import { getBreadcrumbListJsonLd } from '@scripts/seo/getBreadcrumbListJsonLd';
import { themeColor } from '@scripts/themes/cssVars';

import { getVirtualFilters } from '../helper';
import CategoryCategoriesList from './CategoryCategoriesList';
import EmptyProductsPlaceholder from './EmptyProductsPlaceholder';
import styles from './SubCategory.module.scss';
import VirtualCategories from './VirtualCategories';

const SubCategory = ({
    isVirtual,
    categoryId,
    totalSSR,
    dataProducts,
    seoData,
    categoryVirtualDetailData,
    categoriesVirtualDetailData,
    filters,
    isNotFound,
    isError = false,
}: {
    isVirtual?: boolean;
    categoryId?: number | null;
    totalSSR?: number;
    dataProducts?: any;
    seoData?: SeoData;
    categoryVirtualDetailData?: { data: VirtualCategory };
    categoriesVirtualDetailData?: { data: CategoryDetail };
    filters?: ProductsFilterItem;
    isNotFound?: boolean;
    isError?: boolean;
}) => {
    const productsListDataBack = dataProducts?.data || [];
    const [productDetailsData, setProductDetailsData] = useState<ProductDetail[]>(productsListDataBack || []);
    const [activeView, setActiveView] = useState<VIEW_OPTIONS>(VIEW_OPTIONS.DEFAULT);
    const lastProductsListDataRef = useRef<ProductDetail[] | null>(null);
    const lastProcessedQueryRef = useRef<string | null>(null);

    const { asPath, query } = useRouter();
    const currentCity = useCityStore(({ currentCity }) => currentCity);

    const pathname = getCorrectPathname(asPath);

    const seoItemId = getIdBySeoType(pathname, query);
    const { data: { title = '', description = '', h1 = '', seo_text = '' } = {} } = seoData || {};

    const activePage = +(query?.page || 1);

    const categoryCode = String(isVirtual ? query.code || '' : query.category || '');

    const categoryVirtualId = categoryId || seoItemId;

    const { apiUser, error: userError, user } = useGetUserData();

    const [sortFilter, setSortFilter] = useState<SortFilter>(() => {
        const filters = safeParseFilters(query.filters);

        return {
            sort: readStoredCatalogSort(pathname, POPULAR_SORT_VALUE),
            isSale: filters.isSale || false,
            isPreOrder: filters.isPreOrder === false,
        };
    });

    const virtualCat = categoryVirtualDetailData?.data;

    const { preparedFilters, filtersListData: clientFiltersListData } = useCatalogFilters(
        isVirtual ? { virtual_category_id: categoryVirtualId } : { category_id: categoryId },
        {
            enabled: Boolean(categoryVirtualId || categoryId) && !user && !filters,
            enabledAuth: Boolean(categoryVirtualId || categoryId) && Boolean(apiUser) && !userError && !filters,
            keepPreviousData: true,
        }
    );

    const filtersListData = filters || clientFiltersListData;
    const totalPages = getTotalPages(dataProducts, ITEMS_PER_PRODUCTS_PAGE);

    const hasCategory = categoryVirtualId !== undefined || categoryId !== undefined;

    const enabled = hasCategory && !user;
    const enabledAuth = hasCategory && Boolean(apiUser) && !userError;

    const {
        productsList: productsListData,
        total,
        isFetchingProducts,
        isFetchingProductsAuth,
    } = useGetProductsList(
        {
            sort: sortFilter.sort,
            ...((categoryVirtualId || categoryId) !== null && {
                filter: {
                    ...preparedFilters,
                    ...(isVirtual
                        ? { virtual_category_id: categoryVirtualId || categoryId }
                        : { category_id: categoryId }),
                    ...(sortFilter.isSale && { has_discount: sortFilter.isSale }),
                    ...(sortFilter.isPreOrder && getInStockFilter()),
                },
            }),
            customer_region_guid: currentCity.city_guid,
            pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, activePage),
            include: ['brand', 'category', 'images', 'attributes', 'seller'],
        },
        { enabled, enabledAuth, keepPreviousData: true }
    );

    const { data: apiDataCategories } = useCategoryDetail(
        {
            ...(isVirtual
                ? {
                      id: Number(
                          virtualCat?.parent_virtual_category?.parent_pim_category_id ||
                              virtualCat?.parent_pim_category_id
                      ),
                  }
                : { code: categoryCode }),
            ...(!isVirtual &&
                isNotEmptyObject(preparedFilters) && {
                    offers_filter: preparedFilters,
                }),
        },
        isVirtual ? Boolean(virtualCat) : true
    );

    const apiDataCategory = categoriesVirtualDetailData?.data || apiDataCategories?.data;

    const pimCat = apiDataCategory;
    const category = isVirtual ? virtualCat : pimCat;

    const categorySort = useMemo(() => {
        if (isVirtual) {
            return [];
        }

        const children = (category as CategoryDetail)?.children ?? [];

        return [...children].sort((a, b) => {
            const aSort = a.sort;
            const bSort = b.sort;
            const aHasSort = aSort !== null && aSort !== undefined;
            const bHasSort = bSort !== null && bSort !== undefined;

            if (!aHasSort && !bHasSort) {
                return a.id - b.id;
            }

            if (!aHasSort) {
                return 1;
            }

            if (!bHasSort) {
                return -1;
            }

            if (aSort !== bSort) {
                return aSort - bSort;
            }

            return a.id - b.id;
        });
    }, [isVirtual, category]);

    const preExpandedFilters = useMemo(() => {
        if (!virtualCat) return;

        return getVirtualFilters(virtualCat);
    }, [virtualCat]);

    const baseFilters = useMemo((): FormValues | undefined => {
        if (isVirtual) {
            const virtualId = categoryVirtualId || categoryId;
            if (!virtualId) return undefined;

            return { virtual_category_id: virtualId };
        }

        if (!categoryId) {
            return undefined;
        }

        return { category_id: categoryId };
    }, [isVirtual, categoryVirtualId, categoryId]);

    const breadcrumbsItems = !isVirtual
        ? [
              {
                  label: 'Главная',
                  href: '/',
              },
              ...getBreadcrumbs(
                  [
                      pimCat?.parents?.name
                          ? {
                                name: pimCat?.parents?.name,
                                code: pimCat?.parents?.code,
                            }
                          : null,
                      ...customFlatWithoutChildren(pimCat?.parents?.children),
                      pimCat?.parents?.name
                          ? { code: pimCat?.code, name: pimCat?.name }
                          : { code: category?.code, name: category?.name },
                  ].filter(getBreadcrumb => getBreadcrumb)
              ),
          ]
        : [
              {
                  label: 'Главная',
                  href: '/',
              },
              ...getBreadcrumbs([
                  {
                      name: pimCat?.parents?.name,
                      code: pimCat?.parents?.code,
                  },
                  ...customFlatWithoutChildren(pimCat?.parents?.children),
                  { code: pimCat?.code, name: pimCat?.name },
                  ...((virtualCat?.parent_virtual_category && [
                      {
                          code: `collections/${virtualCat.parent_virtual_category.code}`,
                          name: virtualCat?.parent_virtual_category.name,
                      },
                  ]) ||
                      []),
                  { code: `collections/${category?.code}`, name: category?.name },
              ]),
          ];

    const breadcrumbsJsonLd = useMemo(
        () => getBreadcrumbListJsonLd(breadcrumbsItems, getSiteOrigin()),
        [breadcrumbsItems]
    );

    const getQueryKey = () =>
        JSON.stringify({
            filters: query.filters,
            sort: query.sort,
            page: query.page,
        });

    const currentQueryKey = getQueryKey();
    const isPendingQueryChange =
        lastProcessedQueryRef.current !== null && currentQueryKey !== lastProcessedQueryRef.current;
    const isFiltersLoading = isPendingQueryChange || isFetchingProducts || isFetchingProductsAuth;

    const hasProducts =
        (productDetailsData && productDetailsData.length > 0) || (productsListData && productsListData.length > 0);

    useEffect(() => {
        if (!productsListData) return;
        if (productsListData.length === 0) {
            setProductDetailsData([]);
            lastProductsListDataRef.current = productsListData;
            lastProcessedQueryRef.current = getQueryKey();

            return;
        }

        if (productsListData === lastProductsListDataRef.current) return;
        lastProductsListDataRef.current = productsListData;
        lastProcessedQueryRef.current = getQueryKey();

        const isLoadMore = query.isLoadMoreAvailable === 'true';

        if (productDetailsData.length === 0 || !isLoadMore) {
            setProductDetailsData([...productsListData]);
        } else {
            setProductDetailsData(prev => {
                const combined = [...prev, ...productsListData];

                return [...new Map(combined.map(item => [item.id, item])).values()];
            });
        }
    }, [productsListData, query.filters, query.sort, query.page, query.isLoadMoreAvailable]);

    useEffect(() => {
        if (productDetailsData.length > 0 && lastProcessedQueryRef.current === null) {
            lastProcessedQueryRef.current = getQueryKey();
        }
    }, [productDetailsData.length]);

    useEffect(() => {
        lastProductsListDataRef.current = null;
        lastProcessedQueryRef.current = null;

        return () => {
            setProductDetailsData([]);
        };
    }, [pathname]);

    useEffect(() => {
        const filters = safeParseFilters(query.filters);

        setSortFilter(prev => ({
            ...prev,
            isSale: filters.has_discount || false,
            isPreOrder: filters.isPreOrder === false,
        }));
    }, [query.filters]);

    return (
        <PageWrapper
            isNotFound={isNotFound}
            isError={isError}
            seoProps={{
                activePage,
                totalPages,
                title:
                    title ||
                    `${(isVirtual ? virtualCat?.full_name : category?.name) || ''} купить в интернет-магазине «Layta»`,
                description:
                    description ||
                    `${category?.name} различных производителей и брендов купить по выгодным ценам с доставкой в любой регион России в интернет-магазине «Layta».`,
                nameDigi: 'category',
                contentDigi: 'Digi Category Page',
            }}
            className="any-category-page"
            dataCategoryId={categoryId}
        >
            <JsonLdScript data={breadcrumbsJsonLd} />
            {category && breadcrumbsItems.length > 0 && (
                <Section css={{ flex: '0 0 auto', minHeight: 0, height: 'fit-content' }}>
                    <Layout cols={{ xxxl: 12, sm: 6 }}>
                        <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                            <Breadcrumbs items={breadcrumbsItems} withPageOffset />
                        </Layout.Item>
                    </Layout>
                </Section>
            )}
            <Section
                css={{
                    flex: 1,
                    minHeight: 0,
                    ...(!hasProducts && { backgroundColor: themeColor.backgroundEmpty }),
                }}
            >
                {category && (
                    <LayoutWithMargin cols={{ xxxl: 12, sm: 6 }} gap="24px">
                        {hasProducts ? (
                            <>
                                <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                    <div className={styles.titleRow}>
                                        <h1 className={styles.title}>
                                            {h1 || (isVirtual ? virtualCat?.full_name : category.name)}
                                        </h1>

                                        {totalSSR !== 0 && (total || totalSSR) && (
                                            <span className={styles.productsCount}>
                                                {total || totalSSR}{' '}
                                                {(!!total || !!totalSSR) &&
                                                    declOfNum(
                                                        isVirtual
                                                            ? total || totalSSR || 0
                                                            : (category as CategoryDetail)?.offers_count,
                                                        ['товар', 'товара', 'товаров']
                                                    )}
                                            </span>
                                        )}
                                    </div>
                                </Layout.Item>
                                {!isVirtual && (categorySort as CategoryDetail[]).length > 0 && (
                                    <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                        <CategoryCategoriesList categories={categorySort as CategoryDetail[]} />
                                    </Layout.Item>
                                )}
                                <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                    <ProductsList
                                        activeView={activeView}
                                        setActiveView={setActiveView}
                                        productsListData={
                                            productDetailsData && productDetailsData.length > 0
                                                ? productDetailsData
                                                : productsListData
                                        }
                                        totalPages={totalPages}
                                        filters={filtersListData}
                                        sortFilter={sortFilter}
                                        setSortFilter={setSortFilter}
                                        preExpandedFilters={preExpandedFilters}
                                        setProductDetailsData={setProductDetailsData}
                                        baseFilters={baseFilters}
                                        totalSSR={totalSSR}
                                        isFilterMetricsEnabled
                                        isFiltersLoading={isFiltersLoading}
                                    />
                                </Layout.Item>
                                {isVirtual && category?.children && category?.children?.length > 0 && (
                                    <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                        <VirtualCategories
                                            virtual_categories={(category as VirtualCategory).children!}
                                        />
                                    </Layout.Item>
                                )}
                                {!isVirtual && pimCat?.virtual_categories?.length ? (
                                    <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                        <VirtualCategories virtual_categories={pimCat.virtual_categories} />
                                    </Layout.Item>
                                ) : null}
                                <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                    {seo_text && (
                                        <ExpandableBlock>
                                            <span
                                                css={css`
                                                    margin-top: 48px;
                                                    ul li {
                                                        list-style-type: disc !important;
                                                        list-style-position: inside !important;
                                                    }
                                                    ol li {
                                                        list-style-type: decimal !important;
                                                        list-style-position: inside !important;
                                                    }
                                                `}
                                                dangerouslySetInnerHTML={{ __html: seo_text }}
                                            />
                                        </ExpandableBlock>
                                    )}
                                </Layout.Item>
                            </>
                        ) : (
                            <Layout.Item col={{ xxxl: 12, sm: 6 }}>
                                <EmptyProductsPlaceholder isLoading={isFiltersLoading} />
                            </Layout.Item>
                        )}
                    </LayoutWithMargin>
                )}
            </Section>
            <Section>
                <div id="digi-recs" />
                <div className="category_page.personalised"></div>
            </Section>
        </PageWrapper>
    );
};

export default SubCategory;
